import React, { useEffect, useState } from "react";
import { Plus, Trash2, UserCog, Wrench, Users } from "lucide-react";
import { Link } from "react-router-dom";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { techniciansApi, formatApiError } from "../lib/api";

export const AdminPage = () => {
  const [technicians, setTechnicians] = useState([]);
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    try { setTechnicians(await techniciansApi.list()); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { refresh(); }, []);

  const handleAdd = async (e) => {
    e.preventDefault();
    const name = newName.trim();
    if (!name) return;
    if (technicians.includes(name)) { toast.error("Tekniker finns redan."); return; }
    setBusy(true);
    try {
      const next = await techniciansApi.add(name);
      setTechnicians(next);
      setNewName("");
      toast.success(`${name} tillagd`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const handleDelete = async (name) => {
    if (!window.confirm(`Ta bort ${name} från tekniker-listan?`)) return;
    try {
      const next = await techniciansApi.remove(name);
      setTechnicians(next);
      toast.success(`${name} borttagen`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <section data-testid="admin-page"
             className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 sm:pt-8 pb-24 fade-in-up">
      <div className="flex items-center gap-3 mb-2">
        <span className="inline-flex items-center justify-center rounded-xl p-2.5"
              style={{ backgroundColor: "#3B9EDA" }}>
          <UserCog className="h-5 w-5 text-white" />
        </span>
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold display-tight"
              style={{ color: "#0F172A", fontFamily: "Manrope, sans-serif" }}
              data-testid="admin-title">
            Admin
          </h1>
          <p className="text-slate-500 text-sm mt-1.5">
            Hantera tekniker-lista och användarkonton.
          </p>
        </div>
      </div>

      <Link to="/admin/anvandare" data-testid="link-users-admin"
            className="soft-card press-feedback p-5 mt-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="h-10 w-10 rounded-full bg-indigo-50 text-indigo-600 inline-flex items-center justify-center">
            <Users className="h-5 w-5" />
          </span>
          <div>
            <h3 className="font-semibold text-slate-900">Användarkonton</h3>
            <p className="text-xs text-slate-500 mt-0.5">Skapa, inaktivera och återställ konton för medarbetare.</p>
          </div>
        </div>
        <span className="text-sm text-[#3B9EDA] font-semibold">Hantera →</span>
      </Link>

      <h2 className="font-semibold text-slate-500 mt-8 mb-3 text-[11px] uppercase tracking-[0.08em]">
        Tekniker-lista
      </h2>
      <div className="soft-card p-6">
        <form onSubmit={handleAdd} className="flex flex-col sm:flex-row gap-3 mb-6">
          <Input data-testid="admin-tech-input" placeholder="Namn på ny tekniker…"
                 value={newName} onChange={(e) => setNewName(e.target.value)}
                 className="rounded-full px-5 min-h-[44px]" />
          <Button data-testid="admin-add-btn" type="submit" disabled={busy || !newName.trim()}
                  className="press-feedback rounded-full px-6 text-white whitespace-nowrap min-h-[44px]"
                  style={{ backgroundColor: "#3B9EDA" }}>
            <Plus className="h-4 w-4 mr-1.5" /> Lägg till
          </Button>
        </form>
        {technicians.length === 0 ? (
          <div data-testid="admin-empty-state" className="text-center py-10 text-slate-500">
            <Wrench className="h-10 w-10 mx-auto mb-2 text-slate-300" />
            <p>Inga tekniker tillagda ännu.</p>
          </div>
        ) : (
          <ul data-testid="admin-tech-list" className="divide-y divide-slate-100">
            {technicians.map((t) => (
              <li key={t} data-testid={`admin-tech-row-${t}`}
                  className="flex items-center justify-between py-3">
                <span className="flex items-center gap-3">
                  <span className="h-9 w-9 rounded-full bg-[#E6F1FB] text-[#0C447C] inline-flex items-center justify-center font-semibold text-sm">
                    {t.slice(0, 1).toUpperCase()}
                  </span>
                  <span className="font-medium text-slate-800">{t}</span>
                </span>
                <button data-testid={`admin-delete-${t}`} onClick={() => handleDelete(t)}
                        className="press-feedback text-slate-400 hover:text-red-600 p-2 rounded-full min-h-[40px] min-w-[40px] inline-flex items-center justify-center"
                        aria-label={`Ta bort ${t}`}>
                  <Trash2 className="h-4 w-4" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
};

export default AdminPage;
